import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as documentRequestsApi from '../../api/documents';
import * as clientsApi from '../../api/clients';
import { formatDate } from '../../utils/format';

export default function DocumentRequests() {
  const { actor } = useAuth();
  const isClient = actor.role === 'client';
  const canRequest = actor.role === 'super_admin' || actor.role === 'admin' || actor.role === 'employee';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ clientId: '', documentName: '', notes: '' });
  const [clientOptions, setClientOptions] = useState([]);

  const [fulfilTarget, setFulfilTarget] = useState(null);
  const [fulfilFile, setFulfilFile] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    documentRequestsApi
      .listDocumentRequests({})
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load document requests.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = async () => {
    setForm({ clientId: '', documentName: '', notes: '' });
    if (clientOptions.length === 0) {
      const res = await clientsApi.listClients({ limit: 500 });
      setClientOptions(res.data.map((c) => ({ value: c.id, label: c.clientName })));
    }
    setShowForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await documentRequestsApi.createDocumentRequest(form);
      setSuccess('Document request sent to client.');
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create request.');
    }
  };

  const handleFulfil = async (e) => {
    e.preventDefault();
    if (!fulfilFile) {
      setError('Please choose a file.');
      return;
    }
    try {
      await documentRequestsApi.fulfilDocumentRequest(fulfilTarget.id, fulfilFile);
      setSuccess('Document uploaded for this request.');
      setFulfilTarget(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload document.');
    }
  };

  const handleReview = async (row) => {
    try {
      await documentRequestsApi.reviewDocumentRequest(row.id);
      setSuccess('Marked as reviewed.');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark as reviewed.');
    }
  };

  const columns = [
    { key: 'documentName', label: 'Document' },
    { key: 'client', label: 'Client', render: (r) => r.client?.clientName || '-' },
    { key: 'requestedBy', label: 'Requested By', render: (r) => r.requestedBy?.name || '-' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'requestDate', label: 'Requested On', render: (r) => formatDate(r.requestDate) },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="btn-group">
          {isClient && r.status === 'Pending' && (
            <button type="button" className="btn-link" onClick={() => setFulfilTarget(r)}>Upload</button>
          )}
          {!isClient && r.status === 'Uploaded' && (
            <button type="button" className="btn-link" onClick={() => handleReview(r)}>Mark Reviewed</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Document Requests">
      <div className="page-header">
        <div>
          <h1 className="heading">Document Requests</h1>
          <p className="page-subtext">Track documents requested from clients, end to end.</p>
        </div>
        {canRequest && <button type="button" className="btn" onClick={openCreate}>Request Document</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {loading ? <Loader text="Loading requests..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No document requests yet." />}

      {showForm && (
        <Modal
          title="Request a Document"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="request-form" className="btn">Send Request</button>
            </>
          }
        >
          <form id="request-form" onSubmit={handleCreate}>
            <div className="form-grid single-column">
              <FormField label="Client" type="select" options={clientOptions} value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} required />
              <FormField label="Document Name" value={form.documentName} onChange={(e) => setForm({ ...form, documentName: e.target.value })} required hint="E.g. Bank Statement - March 2026" />
              <FormField label="Notes" type="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}

      {fulfilTarget && (
        <Modal
          title={`Upload - ${fulfilTarget.documentName}`}
          onClose={() => setFulfilTarget(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setFulfilTarget(null)}>Cancel</button>
              <button type="submit" form="fulfil-form" className="btn">Upload</button>
            </>
          }
        >
          <form id="fulfil-form" onSubmit={handleFulfil}>
            <FormField label="File" type="file" onChange={(e) => setFulfilFile(e.target.files[0])} required />
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
